import { useState } from "react";
import { uploadFile } from "../lib/upload";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Upload, Link as LinkIcon, Check, X, FileVideo, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (!u || u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const pill = (on, tone = "emerald") => `flex-1 h-10 rounded-full text-sm font-semibold border transition-colors ${on ? `border-${tone}-500/50 bg-${tone}-500/15 text-white` : "border-white/10 text-zinc-300 hover:border-white/25"}`;

export function ScreeningQuestionsForm({ questions, answers, onChange }) {
  const [uploading, setUploading] = useState(null);
  if (!questions?.length) return null;
  const get = (id) => answers.find((a) => a.question_id === id) || { question_id: id, value: "", link: "", file_url: "", file_name: "" };
  const set = (id, patch) => onChange([...answers.filter((a) => a.question_id !== id), { ...get(id), ...patch }]);

  const upload = async (q, file) => {
    if (!file) return;
    setUploading(q.id);
    try {
      const data = await uploadFile(file);
      set(q.id, { file_url: data.url, file_name: file.name });
    } catch { /* toast shown by uploadFile */ }
    finally { setUploading(null); }
  };

  return (
    <div className="space-y-4 pt-3 border-t border-white/5" data-testid="screening-questions-form">
      <div className="text-xs uppercase tracking-widest text-zinc-500">Brand's questions <span className="text-nexus-pink">· required</span></div>
      {questions.map((q, i) => {
        const a = get(q.id);
        return (
          <div key={q.id} className="space-y-2" data-testid={`screening-q-${i}`}>
            <div className="text-sm text-zinc-200">{i + 1}. {q.label}</div>
            {q.type === "yes_no" && (
              <div className="flex gap-2">
                <button type="button" onClick={() => set(q.id, { value: "yes" })} className={pill(a.value === "yes", "emerald")} data-testid={`screening-q-${i}-yes`}><Check className="inline h-4 w-4 mr-1" /> Yes</button>
                <button type="button" onClick={() => set(q.id, { value: "no" })} className={pill(a.value === "no", "red")} data-testid={`screening-q-${i}-no`}><X className="inline h-4 w-4 mr-1" /> No</button>
              </div>
            )}
            {q.type === "text" && (
              <Textarea value={a.value} onChange={(e) => set(q.id, { value: e.target.value })} rows={2} placeholder="Your answer…" className="bg-white/[0.02] border-white/10 text-white" data-testid={`screening-q-${i}-text`} />
            )}
            {q.type === "upload" && (
              <div className="space-y-2">
                <label className={`flex items-center justify-center gap-2 h-11 rounded-xl border border-dashed text-sm cursor-pointer transition-colors ${a.file_url ? "border-emerald-500/40 text-emerald-200 bg-emerald-500/5" : "border-white/15 text-zinc-300 hover:border-white/30"}`} data-testid={`screening-q-${i}-upload`}>
                  {a.file_url ? <><FileVideo className="h-4 w-4" /> {a.file_name || "File attached"} · tap to replace</> : <><Upload className="h-4 w-4" /> {uploading === q.id ? "Uploading…" : "Upload a video or file"}</>}
                  <input type="file" accept="video/*,image/*,application/pdf" onChange={(e) => upload(q, e.target.files?.[0])} className="hidden" disabled={uploading === q.id} />
                </label>
                <div className="relative">
                  <LinkIcon className="h-4 w-4 absolute left-3 top-3 text-zinc-500" />
                  <Input value={a.link} onChange={(e) => set(q.id, { link: e.target.value })} placeholder="…or paste a link (TikTok, YouTube, Drive, portfolio)" className="pl-9 bg-white/[0.02] border-white/10 text-white" data-testid={`screening-q-${i}-link`} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function answersComplete(questions, answers) {
  return (questions || []).every((q) => {
    const a = answers.find((x) => x.question_id === q.id) || {};
    if (q.type === "yes_no") return a.value === "yes" || a.value === "no";
    if (q.type === "text") return (a.value || "").trim().length > 0;
    return (a.link || "").startsWith("http") || (a.file_url || "").startsWith("/api/files/");
  });
}

export function ScreeningAnswers({ answers, compact = false }) {
  if (!answers?.length) return null;
  return (
    <div className={`space-y-1.5 ${compact ? "mt-2" : "mt-3"}`} data-testid="screening-answers">
      {answers.map((a, i) => (
        <div key={a.question_id || i} className="flex items-start gap-2 text-xs" data-testid={`screening-answer-${i}`}>
          <span className="text-zinc-500 shrink-0 max-w-[45%] truncate">{a.label}</span>
          <span className="text-zinc-600">·</span>
          {a.type === "yes_no" ? (
            <span className={`font-semibold inline-flex items-center gap-1 ${a.value === "yes" ? "text-emerald-300" : "text-red-300"}`}>{a.value === "yes" ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {a.value === "yes" ? "Yes" : "No"}</span>
          ) : a.type === "upload" ? (
            <span className="flex flex-wrap gap-2">
              {a.file_url && <a href={abs(a.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-nexus-blue hover:underline"><FileVideo className="h-3 w-3" /> {a.file_name || "File"}</a>}
              {a.link && <a href={a.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-nexus-blue hover:underline truncate max-w-[220px]"><ExternalLink className="h-3 w-3" /> {a.link.replace(/^https?:\/\//, "")}</a>}
            </span>
          ) : (
            <span className="text-zinc-200 whitespace-pre-wrap">{a.value}</span>
          )}
        </div>
      ))}
    </div>
  );
}
